import type { FaceBasis } from "../modeling/faceBasis.ts";
import {
  joinedSurfaceBasis,
  joinedSurfaceBasisForHits,
  surfaceHitBasis,
} from "../modeling/surfaceSelection.ts";
import {
  queryViewportSurface,
  type ViewportSurfaceHit,
} from "../modeling/viewportSurfaceRaycast.ts";

export interface SurfaceSelectionInputResult {
  basis: FaceBasis;
  hit: ViewportSurfaceHit;
  hostObjectId: string;
}

function ndcFromClient(
  clientX: number,
  clientY: number,
  rect: DOMRect,
): { ndcX: number; ndcY: number } {
  return {
    ndcX: ((clientX - rect.left) / rect.width) * 2 - 1,
    ndcY: -((clientY - rect.top) / rect.height) * 2 + 1,
  };
}

export function resolveSurfaceSelectionAtPointer(
  canvas: HTMLCanvasElement,
  clientX: number,
  clientY: number,
): SurfaceSelectionInputResult | null {
  const rect = canvas.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;
  const { ndcX, ndcY } = ndcFromClient(clientX, clientY, rect);
  const hit = queryViewportSurface(ndcX, ndcY);
  if (!hit) return null;
  const basis = surfaceHitBasis(hit);
  if (!basis) return null;
  return { basis, hit, hostObjectId: hit.objectId };
}

export function resolveJoinedSurfaceSelectionAtPointer(
  canvas: HTMLCanvasElement,
  clientX: number,
  clientY: number,
  selectedHits: readonly ViewportSurfaceHit[],
): SurfaceSelectionInputResult | null {
  if (selectedHits.length === 0 || selectedHits.length >= 3) return null;
  const rect = canvas.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;
  const { ndcX, ndcY } = ndcFromClient(clientX, clientY, rect);
  const hit = queryViewportSurface(ndcX, ndcY);
  if (!hit) return null;
  const hits = [...selectedHits, hit];
  const basis =
    hits.length === 2
      ? joinedSurfaceBasis(hits[0]!, hits[1]!)
      : joinedSurfaceBasisForHits(hits);
  if (!basis) return null;
  return { basis, hit, hostObjectId: selectedHits[0]!.objectId };
}
